import type { ProductSetupConfig, SetupProgress } from "../registry/types";

/**
 * Pure derivation of which floating setup-guide surfaces should render,
 * given current state. Extracted from `setup-guide-host.tsx` so the
 * route-scoping rules are testable in isolation without mounting the
 * full host + provider tree.
 *
 * Surfaces:
 *   - `showIntakeModal`: blocking intake modal. Renders only inside the
 *     active product's `routePattern`.
 *   - `showResumeReminder`: bottom-right "Resume <Product>" affordance.
 *     Renders when intake is pending + we're outside the product's
 *     routes — replacing the intrusive modal with a single tap-target.
 *   - `showWidget`: existing post-intake checklist widget. Renders only
 *     when intake is done AND a row exists AND the widget is expanded.
 *   - `showContextualModals`: per-task contextual modals. Same scoping
 *     as the intake modal — only inside the product's routes.
 *
 * Invariants:
 *   - Only one of `showIntakeModal` / `showResumeReminder` is ever true
 *     at once.
 *   - `showWidget` only ever true when both `showIntake* surfaces are
 *     false (intake completed → widget takes over).
 */
export function computeHostSurfaces(args: {
  activeProduct: string | null;
  activeConfig: ProductSetupConfig | null;
  activeProgress: SetupProgress | null;
  showIntake: boolean;
  widgetVisualState: "expanded" | "collapsed" | "hidden";
  pathname: string;
}): {
  inProductRoutes: boolean;
  showIntakeModal: boolean;
  showResumeReminder: boolean;
  showWidget: boolean;
  showContextualModals: boolean;
} {
  const {
    activeProduct,
    activeConfig,
    activeProgress,
    showIntake,
    widgetVisualState,
    pathname,
  } = args;

  if (!activeProduct || !activeConfig) {
    return {
      inProductRoutes: false,
      showIntakeModal: false,
      showResumeReminder: false,
      showWidget: false,
      showContextualModals: false,
    };
  }

  const inProductRoutes = activeConfig.routePattern.test(pathname);

  // Widget renders only after intake is genuinely completed. Gating on
  // `intakeCompletedAt != null` (rather than just `!showIntake`) is what
  // distinguishes a completed merchant from a deferred one — both have
  // showIntake=false, but a deferred merchant hasn't actually answered
  // the questions yet, so the post-intake task checklist would be a
  // confusing 0%-progress widget. (Permanent dismissal is already
  // handled by `widget.visualState` flipping to "hidden".)
  const intakeCompleted = activeProgress?.intakeCompletedAt != null;

  return {
    inProductRoutes,
    showIntakeModal: showIntake && inProductRoutes,
    showResumeReminder: showIntake && !inProductRoutes,
    showWidget:
      !showIntake &&
      intakeCompleted &&
      activeProgress != null &&
      widgetVisualState === "expanded",
    showContextualModals: inProductRoutes,
  };
}
